
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import GET_CONTRACTORS from "@/apollo/autocomplete-queries/GetContractors.gql";
import GET_CONTRACTORS_WITH_PRIVATE_DATA from "@/apollo/autocomplete-queries/GetContractorsWithPrivateData.gql";
import DropdownOption from "@/entities/DropdownOption";
import { Contractor, Query_RootContractorArgs, User } from "@/generated/graphql";
import getMultiselectEmployees from "@/helpers/getMultiselectEmployees";
import EmployeeMultiselect from "@/components/autocompleteMultiselects/EmployeeMultiselectNew.vue";
import MultiselectAvatar from "@/components/autocompleteMultiselects/MultiselectAvatar.vue";
import MultiselectAvatarWithBookmark from "@/components/autocompleteMultiselects/MultiselectAvatarWithBookmark.vue";
import EmployeeDropdownOption from "@/entities/EmployeeDropdownOption";
import getUserFullData from "@/helpers/getUserFullData";
import S3Image from "@/components/common/s3-image.vue";
import BookmarkIcon from "@/components/common/BookmarkIcon.vue";

@Component({
  components: {
    EmployeeMultiselect,
    MultiselectAvatar,
    MultiselectAvatarWithBookmark,
    S3Image,
    BookmarkIcon,
  },
  apollo: {
    contractorsPublic: {
      query: GET_CONTRACTORS,
      variables() {
        return this.contractorAutocompleteFilter;
      },
      update(data) {
        return data.contractor;
      },
      skip() {
        return this.withEmailSearch;
      },
    },
    contractorsPrivate: {
      query: GET_CONTRACTORS_WITH_PRIVATE_DATA,
      variables() {
        return this.contractorAutocompleteFilter;
      },
      update(data) {
        return data.contractor;
      },
      skip() {
        return !this.withEmailSearch;
      },
    },
  },
})
export default class ContractorsMultiselectWithBookmarks extends Vue {
  @Prop({ required: true, type: Array }) value!: string[];
  @Prop({ default: true, type: Boolean }) withBorder!: boolean;
  @Prop({ default: true, type: Boolean }) multiple!: boolean;
  @Prop({ default: false, type: Boolean }) invalid!: boolean;
  @Prop({ default: false, type: Boolean }) disabled!: boolean;
  @Prop({ type: Number, default: undefined }) readonly max?: number;
  @Prop({ type: String, default: undefined }) readonly placeholder?: string;
  @Prop({ type: String, default: "No contractors" }) readonly noOptionsText?: string;
  @Prop({ type: Object, default: () => ({}) }) contractorsConditions!: Object;
  @Prop({ type: Boolean, default: false }) readonly withEmailSearch!: boolean;
  @Prop({ type: String, default: "" }) readonly getParentcontainerStyle?: string;
  @Prop({ type: Map, default: () => new Map() }) readonly bookmarkedUsers!: Map<string, any>;

  contractorsPublic: Contractor[] = [];
  contractorsPrivate: Contractor[] = [];
  contractorSearchString = "";

  mounted() {
    // Component mounted
  }

  @Watch('bookmarkedUsers', { immediate: true, deep: true })
  onBookmarkedUsersChanged(newBookmarks: Map<string, any>) {
    // Bookmarks updated - component will re-render automatically
  }

  @Watch('contractorsOptions')
  onContractorsOptionsChanged(newOptions: any[]) {
    // Options updated - component will re-render automatically
  }

  get contractors(): Contractor[] {
    return this.withEmailSearch ? this.contractorsPrivate : this.contractorsPublic;
  }

  get isLoading(): boolean {
    const { contractorsPublic, contractorsPrivate } = this.$apollo.queries;
    if (this.withEmailSearch) {
      return contractorsPrivate.loading;
    }
    return contractorsPublic.loading;
  }

  get placeholderText(): string {
    return this.placeholder ? this.placeholder : "Select contractor";
  }

  get contractorsOptions(): DropdownOption[] {
    const options = getMultiselectEmployees({
      employees: this.contractors,
      search: this.contractorSearchString,
      withEmailSearch: this.withEmailSearch,
    });
    return options as DropdownOption[];
  }

  get contractorsOptionsWithBookmarks(): DropdownOption[] {
    const options = this.contractorsOptions;
    
    // Only check bookmarks if we have bookmark data
    if (this.bookmarkedUsers.size === 0) {
      return options;
    }
    
    // Add bookmark information to each option
    const optionsWithBookmarks = options.map((option: any) => {
      const userId = option.userId || option.user_id || option.id;
      const isBookmarked = this.bookmarkedUsers.has(userId);
      return {
        ...option,
        isBookmarked,
        bookmarkIcon: isBookmarked
      };
    });

    // Sort options: bookmarked users first, then regular users
    const sortedOptions = optionsWithBookmarks.sort((a: any, b: any) => {
      // If both are bookmarked or both are not bookmarked, maintain original order
      if (a.isBookmarked === b.isBookmarked) {
        return 0;
      }
      // Bookmarked users come first
      return a.isBookmarked ? -1 : 1;
    });
    
    return sortedOptions;
  }

  get contractorAutocompleteFilter(): Query_RootContractorArgs {
    var currentUser:User = Vue.prototype.$currentUser;
    var args = {}; 
    if (currentUser && currentUser.role === "MANAGER") {
      let argsTemp = {
        approved: { _eq: true },
        company: { managers: { user_id: {_eq: currentUser.id}}}
      };
      args = { ...argsTemp, ...this.contractorsConditions };
    } else {
      args = this.contractorsConditions;
    }
    
    return { where: args };
  }

  get chosenContractors(): EmployeeDropdownOption[] {
    return this.contractors
      .filter(contractor => this.value.some(contractorId => contractor.id === contractorId))
      .map(contractor => {
        const { id, user } = contractor;
        const { given_name, family_name, avatar } = user;
        const email = user.private_data?.email;

        return {
          id,
          name: getUserFullData(given_name!, family_name!, email!),
          given_name,
          family_name,
          avatar,
        } as EmployeeDropdownOption;
      });
  }

  isBookmarked(option: any): boolean {
    // Use the user ID for bookmark checking, not the contractor ID
    const userId = option.userId || option.user_id || option.id;
    return this.bookmarkedUsers.has(userId);
  }

  handleInput(contractors: DropdownOption[]): void {
    const contractorsIds = contractors.map(contractor => contractor.id);
    this.$emit("input", contractorsIds);
  }
}
